import { canonicalizeTaskNumber } from '@internal/spassu-code-contract';

import { appendStreamingContent } from './streamingContent';
import type { StreamUiHandlers } from './streamUiReducer';
import {
  currentIsoTimestamp,
  markTaskIntegrated,
  mergeDiscoveredTasks,
  mergeLoadedTasks,
  updateTaskLastEvent,
  upsertRunningTask,
} from './streamUiTaskHelpers';

type TaskActionType =
  | 'tasks_loaded'
  | 'task_stream_start'
  | 'task_stream_stop'
  | 'tasks_discovered'
  | 'task_started'
  | 'task_progress'
  | 'qa_progress'
  | 'task_completed'
  | 'task_integrated'
  | 'task_error'
  | 'execution_state'
  | 'task_parallel_start'
  | 'task_parallel_progress'
  | 'task_parallel_complete'
  | 'task_parallel_error';

/** Chunk de task formatado para o log: `tool_use` vira linha entre colchetes. */
function formatTaskChunk(
  content: string,
  taskChunkType?: 'text' | 'tool_use',
): string {
  return taskChunkType === 'tool_use' ? `\n[${content}]\n` : content;
}

/** Handlers da execução de tasks (sequencial, paralela) e do QA. */
export const taskHandlers: Pick<StreamUiHandlers, TaskActionType> = {
  tasks_loaded: (state, action) => ({
    ...state,
    tasks: mergeLoadedTasks(action.tasks, state.tasks),
    executionState: action.executionState,
  }),
  task_stream_start: state => ({ ...state, isTaskStreaming: true }),
  task_stream_stop: state => ({ ...state, isTaskStreaming: false }),
  tasks_discovered: (state, action) => ({
    ...state,
    tasks: mergeDiscoveredTasks(state.tasks, action.tasks),
  }),
  task_started: (state, action) => {
    const canonical = canonicalizeTaskNumber(action.taskNumber);
    return {
      ...state,
      tasks: upsertRunningTask(
        state.tasks,
        canonical,
        action.taskTitle ?? `Task ${canonical}`,
      ),
      executionState: 'running',
      streamingTaskNumber: canonical,
      taskStreamingContent: '',
      taskStreamBuffer: '',
      reviewResult: undefined,
      isReviewLoading: false,
    };
  },
  task_progress: (state, action) => {
    const nextBuffer =
      state.taskStreamBuffer +
      formatTaskChunk(action.content, action.taskChunkType);
    return {
      ...state,
      tasks: updateTaskLastEvent(state.tasks, state.streamingTaskNumber),
      taskStreamBuffer: nextBuffer,
      taskStreamingContent: nextBuffer,
    };
  },
  qa_progress: (state, action) => ({
    ...state,
    isTaskStreaming: true,
    executionState: 'running',
    isStreaming: true,
    pendingQuestions: undefined,
    streamingContent: appendStreamingContent(
      state.streamingContent,
      formatTaskChunk(action.content, action.taskChunkType),
    ),
  }),
  task_completed: (state, action) => {
    const executionLog = state.taskStreamBuffer;
    const completedCanonical = canonicalizeTaskNumber(action.taskNumber);
    const completedAt = currentIsoTimestamp();
    return {
      ...state,
      tasks: state.tasks.map(task =>
        canonicalizeTaskNumber(task.taskNumber) === completedCanonical
          ? {
              ...task,
              status: 'completed',
              integrated: false,
              integratedAt: null,
              executionLog,
              completedAt: task.completedAt ?? completedAt,
              lastEventAt: completedAt,
            }
          : task,
      ),
      streamingTaskNumber: null,
      taskStreamingContent: '',
      taskStreamBuffer: '',
    };
  },
  task_integrated: (state, action) => ({
    ...state,
    tasks: markTaskIntegrated(state.tasks, {
      taskNumber: action.taskNumber,
      integratedAt: currentIsoTimestamp(),
      slot: action.slot,
      totalSlots: action.totalSlots,
      worktreePath: action.worktreePath,
    }),
  }),
  task_error: (state, action) => {
    const errorCanonical = action.taskNumber
      ? canonicalizeTaskNumber(action.taskNumber)
      : undefined;
    return {
      ...state,
      tasks: errorCanonical
        ? state.tasks.map(task =>
            canonicalizeTaskNumber(task.taskNumber) === errorCanonical
              ? {
                  ...task,
                  status: 'error',
                  errorMessage: action.errorMessage,
                  lastEventAt: currentIsoTimestamp(),
                }
              : task,
          )
        : state.tasks,
      executionState: 'idle',
      isTaskStreaming: false,
      streamingTaskNumber: null,
      taskStreamingContent: '',
      taskStreamBuffer: '',
    };
  },
  execution_state: (state, action) => ({
    ...state,
    executionState: action.executionState,
  }),
  task_parallel_start: (state, action) => {
    const pCanonical = canonicalizeTaskNumber(action.taskNumber);
    return {
      ...state,
      tasks: upsertRunningTask(
        state.tasks,
        pCanonical,
        action.taskTitle ?? `Task ${pCanonical}`,
        { slot: action.slot, totalSlots: action.totalSlots },
      ),
      executionState: 'running',
      activeTaskSlots: {
        ...state.activeTaskSlots,
        [pCanonical]: {
          content: '',
          buffer: '',
          slot: action.slot,
          totalSlots: action.totalSlots,
        },
      },
    };
  },
  task_parallel_progress: (state, action) => {
    const ppCanonical = canonicalizeTaskNumber(action.taskNumber);
    const existing = state.activeTaskSlots[ppCanonical];
    if (!existing) return state;
    const ppBuffer =
      existing.buffer + formatTaskChunk(action.content, action.taskChunkType);
    return {
      ...state,
      tasks: updateTaskLastEvent(state.tasks, ppCanonical),
      activeTaskSlots: {
        ...state.activeTaskSlots,
        [ppCanonical]: { ...existing, buffer: ppBuffer, content: ppBuffer },
      },
    };
  },
  task_parallel_complete: (state, action) => {
    const pcCanonical = canonicalizeTaskNumber(action.taskNumber);
    const pcSlot = state.activeTaskSlots[pcCanonical];
    const pcLog = pcSlot?.buffer ?? '';
    const pcCompletedAt = currentIsoTimestamp();
    const { [pcCanonical]: _removed, ...remainingSlots } =
      state.activeTaskSlots;
    return {
      ...state,
      tasks: state.tasks.map(task =>
        canonicalizeTaskNumber(task.taskNumber) === pcCanonical
          ? {
              ...task,
              status: 'completed' as const,
              integrated: false,
              integratedAt: null,
              executionLog: pcLog,
              parallelSlot: pcSlot?.slot ?? task.parallelSlot ?? null,
              totalSlots: pcSlot?.totalSlots ?? task.totalSlots ?? null,
              completedAt: task.completedAt ?? pcCompletedAt,
              lastEventAt: pcCompletedAt,
            }
          : task,
      ),
      activeTaskSlots: remainingSlots,
    };
  },
  task_parallel_error: (state, action) => {
    const peCanonical = canonicalizeTaskNumber(action.taskNumber);
    const { [peCanonical]: _removedErr, ...remainingErrSlots } =
      state.activeTaskSlots;
    return {
      ...state,
      tasks: state.tasks.map(task =>
        canonicalizeTaskNumber(task.taskNumber) === peCanonical
          ? {
              ...task,
              status: 'error' as const,
              errorMessage: action.errorMessage,
              lastEventAt: currentIsoTimestamp(),
            }
          : task,
      ),
      activeTaskSlots: remainingErrSlots,
    };
  },
};
